import './summarystep.less'

import {Icon} from '@codeurs/front'
import {Form, Formik} from 'formik'
import requestService from 'mithril/request/request'
import {RegistrationModel} from 'models/registration/RegistrationModel'
import {StepKey} from 'models/registration/StepModel'
import {DetailsInfoState} from 'models/registration/steps/DetailsStepModel'
import {
	ConfirmationData,
	SummaryStepModel,
	SummaryStepState
} from 'models/registration/steps/SummaryStepModel'
import React, {useEffect, useState} from 'react'
import useReactRouter from 'use-react-router'
import {formatPrice} from 'util/currency'
import {FormikChanges} from 'util/formikchanges'
import {useTranslation} from 'util/i18n'
import {style} from 'util/style'
import {FormField} from 'views/form/form'
import {Step} from 'views/registration/step'
import {Theme} from 'views/theme'
import {Paypal} from '../paypal'

const request = requestService(window, Promise).request

const styles = style('summarystep', ['error', 'price', 'content', 'payment'])

export type ConfirmationOption = {
	key: string
	label: string
	type: 'pack' | 'option'
	price: {
		excl: number
		vat: number
		incl: number
	}
}

export type ConfirmationState =
	| {type: 'error'}
	| ({type: 'success'} & ConfirmationData & {
				confirmation?: {details: DetailsInfoState}
			})

export const SummaryStep: React.FC<{
	step: StepKey
	registration: RegistrationModel
	updateRegistration: (model: RegistrationModel) => void
}> = attrs => {
	const {registration, updateRegistration} = attrs
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
	const [isChecked, setIsChecked] = useState<boolean>(false)
	const [state, setState] = useState<ConfirmationState>(null)
	const step = registration.allSteps.summary
	const {history} = useReactRouter()
	const t = useTranslation()
	const [errorMessage, setErrorMessage] = useState<string>(null)

	useEffect(() => {
		request({
			method: 'POST',
			url: `/api/register/${registration.id}/summary`,
			data: {...attrs.registration.state, eloqua: registration.getEloquaData()}
		})
			.then(response => {
				setState({
					type: 'success',
					...response,
					confirmed: false,
					price: {
						excl: Math.round(response.price.excl * 100) / 100,
						incl: Math.round(response.price.incl * 100) / 100,
						vat: Math.round(response.price.vat * 100) / 100
					}
				})
				updateRegistration(
					step.setConfirmation({
						options: response.options,
						token: response.token,
						payment: response.payment,
						price: response.price,
						confirmed: response.confirmed
					})
				)
			})
			.catch(error => {
				setState({type: 'error'})
				throw error
			})
	}, [])

	if (!state) {
		return (
			<Step step={step} hasFormSubmit={true}>
				<p>Loading summary...</p>
			</Step>
		)
	}
	if (state.type === 'error') {
		return (
			<Step step={step} hasFormSubmit={true}>
				<p>
					Something went wrong while trying to trying confirm your registration.
					<br />
					Please contact support if this persists.
				</p>
			</Step>
		)
	}

	if (registration.isOnsite) {
		return (
			<Formik<SummaryStepState>
				initialValues={step.state}
				onSubmit={(values, {setErrors}) => {
					history.push(`#${step.nextStepKey}`)
				}}
			>
				<Form>
					<Step step={step} hasFormSubmit={true}>
						<SummaryContent
							step={step}
							updateRegistration={updateRegistration}
							confirmation={state}
						/>
					</Step>
				</Form>
			</Formik>
		)
	}

	if (!state.price.incl) {
		const dataLayer = {
			event: 'purchase',
			ecommerce: {
				item_list_id: registration.config.url.substring(1).replace(/\//g, '_'),
				item_list_name: registration.event.title,
				value: registration.totalValue,
				items: [
					...registration.packs.map(pack => ({
						item_category: 'event',
						item_id: pack.config.key,
						item_name: pack.config.label,
						price: pack.config.price
					})),
					...registration.options
						.map(option => {
							if (!option) return null
							const selected = option.getChoiceFor(registration)
							if (selected) {
								return {
									item_category: option.key,
									item_id: selected.key,
									item_name: selected.label,
									price: selected.config.price
								}
							}
						})
						.filter(i => Boolean(i))
				]
			}
		}

		return (
			<Formik<SummaryStepState>
				initialValues={step.state}
				onSubmit={(values, {setErrors}) => {
					if (isSubmitting) return
					setIsSubmitting(true)
					request({
						method: 'POST',
						url: `/api/register/confirm/${state.token}`
					})
						.then(response => {
							// Purchase dataLayer
							window.dataLayer = window.dataLayer || []
							window.dataLayer.push(dataLayer)

							updateRegistration(registration.clear())
							history.push(step.thanksUrl)
						})
						.catch(error => {
							const errorMessage = JSON.parse(error.message)
							setErrors(errorMessage)
							if (typeof errorMessage === 'string') {
								setErrorMessage(t('error'))
							}
						})
						.finally(() => {
							setIsSubmitting(false)
						})
				}}
			>
				<Form>
					<Step step={step} hasFormSubmit={true} isSubmitting={isSubmitting}>
						<SummaryContent
							step={step}
							updateRegistration={updateRegistration}
							confirmation={{...state, confirmed: true}}
						/>
						<SummaryError message={errorMessage} />
					</Step>
				</Form>
			</Formik>
		)
	}

	return (
		<Formik<SummaryStepState> initialValues={step.state} onSubmit={() => {}}>
			<Form>
				<Step step={step} hideNextButton={true} isSubmitting={isSubmitting}>
					<SummaryContent
						step={step}
						updateRegistration={updateRegistration}
						confirmation={state}
						checked={isChecked}
					/>
					<SummaryError message={errorMessage} />
					<Paypal
						registration={registration}
						confirmation={state}
						setIsChecked={setIsChecked}
						updateRegistration={updateRegistration}
						setErrorMessage={setErrorMessage}
					/>
				</Step>
			</Form>
		</Formik>
	)
}

export const SummaryContent: React.FC<{
	step: SummaryStepModel
	updateRegistration: (model: RegistrationModel) => void
	confirmation: ConfirmationData
	checked?: boolean
}> = attrs => {
	const {step, updateRegistration, confirmation, checked} = attrs

	return (
		<div {...styles.content}>
			<FormikChanges
				onChange={({values}) => {
					const newRegistration = step.setState(values)
					updateRegistration(newRegistration)
				}}
			/>
			<SummaryPrice confirmation={confirmation} step={step} />
			<SummaryPayment step={step} />
			<div {...styles.content('checkboxes').is({checked})}>
				<FormField
					type="checkbox"
					name="agree_terms"
					required={true}
					label={
						<Theme.Wysiwyg>{step.config.terms_and_conditions}</Theme.Wysiwyg>
					}
				/>
				<FormField
					type="checkbox"
					name="agree_privacy"
					required={true}
					label={<Theme.Wysiwyg>{step.config.privacy_policy}</Theme.Wysiwyg>}
				/>
				{step.shouldShowNoShow && (
					<FormField
						type="checkbox"
						name="agree_no_show"
						required={true}
						label={<Theme.Wysiwyg>{step.config.no_show}</Theme.Wysiwyg>}
					/>
				)}
			</div>
		</div>
	)
}

const Amount = ({children}) => (
	<div {...styles.price('amount')}>{formatPrice(children)}</div>
)

export const SummaryPrice: React.FC<{
	step: SummaryStepModel
	confirmation: ConfirmationData
}> = ({step, confirmation}) => {
	if (!step.shouldShowPricing) {
		return (
			<div {...styles.price}>
				<div {...styles.price('heading')}>
					<div {...styles.price('title')}>Name:</div>
					<div>{step.registration.name}</div>
				</div>
				<div {...styles.price('heading')}>
					<div {...styles.price('title')}>Email:</div>
					<div>{step.registration.email}</div>
				</div>
			</div>
		)
	}

	return (
		<div {...styles.price}>
			{confirmation.options.map(item => (
				<div {...styles.price('heading').is({pack: item.type === 'pack'})}>
					<div {...styles.price('title')}>{item.label}</div>
					<Amount>{item.price ? item.price.excl : 0}</Amount>
				</div>
			))}
			<div {...styles.price('heading').mod('divider')}>
				<div {...styles.price('title')}>Total excl. VAT</div>
				<Amount>{confirmation.price.excl}</Amount>
			</div>
			<div {...styles.price('heading')}>
				<div {...styles.price('title')}>VAT</div>
				<Amount>{confirmation.price.vat}</Amount>
			</div>
			<div {...styles.price('heading').mod(['total', 'divider'])}>
				<div {...styles.price('title')}>Total incl. VAT</div>
				<Amount>{confirmation.price.incl}</Amount>
			</div>
		</div>
	)
}

export const SummaryPayment: React.FC<{
	step: SummaryStepModel
}> = ({step}) => {
	if (!step.registration.isOnsite) return null

	return (
		<div {...styles.payment}>
			<h3 {...styles.payment('title')}>{step.methodsLabel}</h3>
			<div {...styles.payment('options')}>
				{step.methods.map(choice => {
					return (
						<FormField
							type="radio"
							label={choice.label}
							value={choice.key}
							name="payment_option"
							required={true}
						/>
					)
				})}
			</div>
		</div>
	)
}

export const SummaryError: React.FC<{message}> = ({message}) => {
	if (!message) return null

	return (
		<div {...styles.error('box')}>
			<div {...styles.error('content')}>
				<div {...styles.error('icon')}>
					<Icon icon="information" />
				</div>
				<div {...styles.error('message')}>{message}</div>
			</div>
		</div>
	)
}
